import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CheckCircle, AlertCircle, Clock, Edit, Calendar, RefreshCw, TrendingUp, TrendingDown, ArrowRight, AlertTriangle, Phone, XCircle, Undo2, Ban, Users, DollarSign, MoveRight } from 'lucide-react';
import MoveCommitmentDialog from '@/components/MoveCommitmentDialog';
import CommitmentTimeline from '@/components/CommitmentTimeline';
import VerificationControls from '@/components/commitments/VerificationControls';
import FulfillmentBlockedModal from '@/components/commitments/FulfillmentBlockedModal';
import CommitmentFilterBar from '@/components/commitments/CommitmentFilterBar';
import NotInterestedDialog from '@/components/commitments/NotInterestedDialog';
import NIApprovalControl from '@/components/commitments/NIApprovalControl';
import { useCommitmentFilter, extractFilterOptions } from '@/hooks/useCommitmentFilter';
import { DatePicker } from '@/components/ui/date-picker';
import { axiosInstance } from '../App';
import { useWtfLiveData } from '../hooks/useWtfLiveData';
import { WtfSyncBadge } from '../components/wtf';

const CommitmentDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [commitments, setCommitments] = useState([]);
  const [loading, setLoading] = useState(true);
  // Initialize filter (status tab) from URL params if provided
  const [filter, setFilter] = useState(() => {
    const urlFilter = searchParams.get('filter');
    return urlFilter && ['pending', 'all', 'fulfilled'].includes(urlFilter) ? urlFilter : 'pending';
  }); // 'pending' | 'all' | 'fulfilled'
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ closure_done: 0, revenue_done: 0 });
  // Date range state (replacing single date)
  const today = new Date().toISOString().split('T')[0];
  // Initialize datePreset from URL params if provided
  const initialDatePreset = (() => {
    const urlDatePreset = searchParams.get('datePreset');
    return urlDatePreset && ['today', 'week', 'month', 'all'].includes(urlDatePreset) ? urlDatePreset : 'today';
  })();
  const [startDate, setStartDate] = useState(() => {
    if (initialDatePreset === 'all') return '';
    if (initialDatePreset === 'week') {
      const d = new Date(); d.setDate(d.getDate() - 7);
      return d.toISOString().split('T')[0];
    }
    if (initialDatePreset === 'month') {
      const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
    }
    return today;
  });
  const [endDate, setEndDate] = useState(initialDatePreset === 'all' ? '' : today);
  const [datePreset, setDatePreset] = useState(initialDatePreset); // 'today' | 'week' | 'month' | 'all' | 'custom'
  // Backward compatibility alias
  const selectedDate = startDate;
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  // Comprehensive insights for all-time analytics
  const [comprehensiveInsights, setComprehensiveInsights] = useState(null);
  const [comprehensiveLoading, setComprehensiveLoading] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);
  const [selectedCommitment, setSelectedCommitment] = useState(null);
  const [blockedModalOpen, setBlockedModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [niDialogOpen, setNiDialogOpen] = useState(false);
  const [niTargetCommitment, setNiTargetCommitment] = useState(null);
  // Multi-dimensional filter state (8 dimensions)
  const [advancedFilters, setAdvancedFilters] = useState({
    gyms: [], tls: [], managers: [], statuses: [],
    verificationStatuses: [], dateRange: { from: null, to: null },
    amountRange: { min: null, max: null }, moveCounts: []
  });
  // Clickable card filters - initialized from URL params
  const [dateFilter, setDateFilter] = useState(() => {
    const urlDateFilter = searchParams.get('dateFilter');
    return urlDateFilter && ['all', 'today', 'past'].includes(urlDateFilter) ? urlDateFilter : 'all';
  });
  const [verificationFilter, setVerificationFilter] = useState(() => {
    const urlVerificationFilter = searchParams.get('verificationFilter');
    return urlVerificationFilter && ['all', 'verifiedByTL', 'verifiedByManager', 'fullyVerified', 'unverified'].includes(urlVerificationFilter)
      ? urlVerificationFilter
      : 'all';
  });

  // Load current user from localStorage
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUser(user);
  }, []);

  // Clear URL params after reading them (so refreshing doesn't keep filters)
  useEffect(() => {
    if (searchParams.get('dateFilter') || searchParams.get('verificationFilter') || searchParams.get('filter') || searchParams.get('datePreset')) {
      // Remove query params from URL without triggering navigation
      setSearchParams({}, { replace: true });
    }
  }, []);

  // Fetch commitments on mount, filter change, or date range change
  useEffect(() => {
    fetchCommitments();
  }, [filter, startDate, endDate]);

  // Fetch insights when date changes (use start date for single-day insights)
  useEffect(() => {
    const fetchInsights = async () => {
      setInsightsLoading(true);
      try {
        const response = await axiosInstance.get(`/commitment-insights?date=${startDate}`);
        setInsights(response.data);
      } catch (error) {
        console.error('Failed to load insights:', error);
      } finally {
        setInsightsLoading(false);
      }
    };
    fetchInsights();
  }, [startDate]);

  // Fetch comprehensive insights when date range changes
  useEffect(() => {
    const fetchComprehensiveInsights = async () => {
      setComprehensiveLoading(true);
      try {
        const params = new URLSearchParams();
        if (startDate) params.append('date_from', startDate);
        if (endDate) params.append('date_to', endDate);
        const response = await axiosInstance.get(`/commitment-insights-comprehensive?${params}`);
        setComprehensiveInsights(response.data);
      } catch (error) {
        console.error('Failed to load comprehensive insights:', error);
      } finally {
        setComprehensiveLoading(false);
      }
    };
    fetchComprehensiveInsights();
  }, [startDate, endDate]);

  const fetchCommitments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      // Use date range instead of single date
      if (startDate) params.append('date_from', startDate);
      if (endDate) params.append('date_to', endDate);
      params.append('view_mode', 'both');  // Show on both original and current dates
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await axiosInstance.get(`/commitments${queryString}`);
      setCommitments(response.data.commitments || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle date preset changes
  const handleDatePreset = (preset) => {
    setDatePreset(preset);
    const todayDate = new Date();
    const todayStr = todayDate.toISOString().split('T')[0];

    switch (preset) {
      case 'today':
        setStartDate(todayStr);
        setEndDate(todayStr);
        break;
      case 'week':
        const weekAgo = new Date(todayDate);
        weekAgo.setDate(weekAgo.getDate() - 7);
        setStartDate(weekAgo.toISOString().split('T')[0]);
        setEndDate(todayStr);
        break;
      case 'month':
        const firstOfMonth = new Date(todayDate.getFullYear(), todayDate.getMonth(), 1);
        setStartDate(firstOfMonth.toISOString().split('T')[0]);
        setEndDate(todayStr);
        break;
      case 'all':
        // "All Time" means NO date filtering - set to empty to skip date params
        setStartDate('');
        setEndDate('');
        break;
      default:
        break;
    }
  };

  // Format currency helper
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleEdit = (commitment) => {
    setEditingId(commitment.id);
    setEditForm({
      closure_done: commitment.closure_done || 0,
      revenue_done: commitment.revenue_done || 0
    });
  };

  const handleSave = async () => {
    try {
      await axiosInstance.patch(`/commitments/${editingId}`, editForm);
      toast.success('Commitment updated successfully');
      setEditingId(null);
      fetchCommitments(); // Refresh data
    } catch (error) {
      const data = error.response?.data;
      if (data?.error === 'no_active_roster' || data?.error === 'not_checked_in') {
        toast.error(data?.detail || 'Please check in to your shift first. Go to My Roster to check in.');
      } else {
        toast.error(data?.detail || data?.error || error.message);
      }
    }
  };

  const handleFulfillClick = (commitment) => {
    // Commitment fulfillment is independent of attendance state (REBUILD-04)
    // Check verification status before allowing fulfillment
    if (!commitment.commitment_tl_verified || !commitment.commitment_mgr_verified) {
      // Show blocked modal - it now has Sync & Verify button for out-of-sync old entries
      setSelectedCommitment(commitment);
      setBlockedModalOpen(true);
      return;
    }

    // If fully verified, proceed with existing fulfill logic
    handleEdit(commitment);
  };

  const handleVerificationComplete = () => {
    // Refresh commitments after verification
    fetchCommitments();
  };

  // Calculate verification statistics including today vs past
  const verificationStats = useMemo(() => {
    const today = selectedDate || new Date().toISOString().split('T')[0];

    // Today vs Past breakdown - use entry_date which is the actual date field
    const getCommitmentDate = (c) => c.entry_date || c.date || c.commitment_date;
    const todayCommitments = commitments.filter(c => getCommitmentDate(c) === today);
    const pastCommitments = commitments.filter(c => getCommitmentDate(c) !== today);

    const todayPendingVerification = todayCommitments.filter(c => !c.commitment_tl_verified || !c.commitment_mgr_verified).length;
    const pastPendingVerification = pastCommitments.filter(c => !c.commitment_tl_verified || !c.commitment_mgr_verified).length;

    // Track pending by manager
    const pendingByManager = {};
    commitments.forEach(c => {
      if (c.commitment_tl_verified && !c.commitment_mgr_verified) {
        const mgrName = c.manager_name || 'Unknown';
        pendingByManager[mgrName] = (pendingByManager[mgrName] || 0) + 1;
      }
    });

    return {
      verifiedByTL: commitments.filter(c => c.commitment_tl_verified && !c.commitment_mgr_verified).length,
      verifiedByManager: commitments.filter(c => c.commitment_mgr_verified && !c.commitment_tl_verified).length,
      verifiedByBoth: commitments.filter(c => c.commitment_tl_verified && c.commitment_mgr_verified).length,
      unverified: commitments.filter(c => !c.commitment_tl_verified && !c.commitment_mgr_verified).length,
      // Today vs Past
      todayCount: todayCommitments.length,
      pastCount: pastCommitments.length,
      todayPendingVerification,
      pastPendingVerification,
      pendingByManager
    };
  }, [commitments, selectedDate]);

  // Filter commitments based on card selections
  const filteredCommitments = useMemo(() => {
    const today = selectedDate || new Date().toISOString().split('T')[0];
    const getCommitmentDate = (c) => c.entry_date || c.date || c.commitment_date;
    let filtered = [...commitments];

    // Apply date filter
    if (dateFilter === 'today') {
      filtered = filtered.filter(c => getCommitmentDate(c) === today);
    } else if (dateFilter === 'past') {
      filtered = filtered.filter(c => getCommitmentDate(c) !== today);
    }

    // Apply verification filter
    if (verificationFilter === 'verifiedByTL') {
      filtered = filtered.filter(c => c.commitment_tl_verified && !c.commitment_mgr_verified);
    } else if (verificationFilter === 'verifiedByManager') {
      filtered = filtered.filter(c => c.commitment_mgr_verified && !c.commitment_tl_verified);
    } else if (verificationFilter === 'fullyVerified') {
      filtered = filtered.filter(c => c.commitment_tl_verified && c.commitment_mgr_verified);
    } else if (verificationFilter === 'unverified') {
      filtered = filtered.filter(c => !c.commitment_tl_verified && !c.commitment_mgr_verified);
    }

    return filtered;
  }, [commitments, selectedDate, dateFilter, verificationFilter]);

  // Handle card clicks
  const handleDateCardClick = (type) => {
    setDateFilter(dateFilter === type ? 'all' : type);
    // Reset verification filter when changing date filter
    if (dateFilter !== type) setVerificationFilter('all');
  };

  const handleVerificationCardClick = (type) => {
    setVerificationFilter(verificationFilter === type ? 'all' : type);
  };

  // Apply multi-dimensional filters via hook
  const advancedFilteredCommitments = useCommitmentFilter(filteredCommitments, advancedFilters);
  const filterOptions = useMemo(() => extractFilterOptions(commitments), [commitments]);
  const activeFilterCount = useMemo(() => {
    return (
      (advancedFilters.gyms?.length || 0) +
      (advancedFilters.tls?.length || 0) +
      (advancedFilters.managers?.length || 0) +
      (advancedFilters.statuses?.length || 0) +
      (advancedFilters.verificationStatuses?.length || 0) +
      (advancedFilters.dateRange?.from ? 1 : 0) +
      (advancedFilters.dateRange?.to ? 1 : 0) +
      (advancedFilters.amountRange?.min !== null && advancedFilters.amountRange?.min !== undefined ? 1 : 0) +
      (advancedFilters.amountRange?.max !== null && advancedFilters.amountRange?.max !== undefined ? 1 : 0) +
      (advancedFilters.moveCounts?.length || 0)
    );
  }, [advancedFilters]);

  // NI handlers
  const handleNotInterested = (commitment) => {
    setNiTargetCommitment(commitment);
    setNiDialogOpen(true);
  };

  const handleReverseNI = async (commitmentId) => {
    try {
      await axiosInstance.post(`/commitments/${commitmentId}/reverse-not-interested`);
      toast.success('Commitment restored to pending status');
      fetchCommitments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reverse Not Interested');
    }
  };

  // WTF lead funnel data integration
  // lead_stats: OBJECT {all_leads:"54337", todays_leads:"30", converted_leads:"7750", total_walkins:"71"}
  const { data: wtfLeadStats, lastSync: wtfSync, stale: wtfStale, loading: wtfLoading } = useWtfLiveData('lead_stats');

  // Parse WTF lead stats (all values are strings)
  const wtfLeadData = useMemo(() => {
    if (!wtfLeadStats || typeof wtfLeadStats !== 'object' || Array.isArray(wtfLeadStats)) {
      return null;
    }
    return {
      convertedLeads: parseInt(wtfLeadStats.converted_leads || '0', 10),
      todaysLeads: parseInt(wtfLeadStats.todays_leads || '0', 10),
      totalWalkins: parseInt(wtfLeadStats.total_walkins || '0', 10),
      allLeads: parseInt(wtfLeadStats.all_leads || '0', 10),
    };
  }, [wtfLeadStats]);

  const getStatusBadge = (status, commitment) => {
    // Handle Not Interested status
    if (status === 'not_interested') {
      if (commitment?.ni_approved) {
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium text-gray-600 bg-gray-100 line-through">
            <XCircle className="w-3 h-3 mr-1" />
            Not Interested
          </span>
        );
      }
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium text-amber-700 bg-amber-50">
          <Clock className="w-3 h-3 mr-1" />
          NI (Pending Approval)
        </span>
      );
    }

    const badges = {
      pending: { icon: Clock, color: 'text-yellow-600 bg-yellow-50', label: 'Pending' },
      partial: { icon: AlertCircle, color: 'text-orange-600 bg-orange-50', label: 'Partial' },
      fulfilled: { icon: CheckCircle, color: 'text-green-600 bg-green-50', label: 'Fulfilled' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const InsightCard = ({ title, value, previousValue, format = 'number', testId }) => {
    const trend = previousValue > 0 ? ((value - previousValue) / previousValue) * 100 : 0;
    const trendColor = trend > 0 ? 'text-green-500' : trend < 0 ? 'text-red-500' : 'text-gray-400';

    const displayValue = format === 'currency'
      ? `₹${(value || 0).toLocaleString('en-IN')}`
      : format === 'percent'
        ? `${value || 0}%`
        : value || 0;

    return (
      <Card className="bg-white" data-testid={testId}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-gray-600">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end justify-between">
            <p className="text-2xl font-bold text-[#1F4E79]">{displayValue}</p>
            <div className={`flex items-center ${trendColor}`}>
              {trend > 0 ? <TrendingUp className="w-4 h-4" /> :
               trend < 0 ? <TrendingDown className="w-4 h-4" /> : null}
              <span className="text-xs ml-1">
                {trend !== 0 && `${trend > 0 ? '+' : ''}${Math.round(trend)}%`}
              </span>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-1">vs previous day</p>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>Commitment Dashboard</CardTitle>
              <p className="text-sm text-gray-500">
                Track and fulfill your closure commitments
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Date Preset Buttons */}
              <div className="flex gap-1">
                {[
                  { key: 'today', label: 'Today' },
                  { key: 'week', label: 'Week' },
                  { key: 'month', label: 'Month' },
                  { key: 'all', label: 'All Time' }
                ].map(preset => (
                  <Button
                    key={preset.key}
                    variant={datePreset === preset.key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleDatePreset(preset.key)}
                    className="h-8"
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
              {/* Date Range Inputs */}
              <div className="flex items-center gap-2">
                <DatePicker
                  date={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    if (e.target.value > endDate) setEndDate(e.target.value);
                    setDatePreset('custom');
                  }}
                  placeholder="From date"
                  className="h-8 w-[140px] text-sm"
                />
                <span className="text-gray-400 text-xs">to</span>
                <DatePicker
                  date={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    if (e.target.value < startDate) setStartDate(e.target.value);
                    setDatePreset('custom');
                  }}
                  placeholder="To date"
                  className="h-8 w-[140px] text-sm"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchCommitments}
                disabled={loading}
                className="h-8 flex items-center gap-1"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Today vs Past Commitments Breakdown - Clickable cards (hide when All Time selected) */}
          {datePreset !== 'all' && (verificationStats.todayCount > 0 || verificationStats.pastCount > 0) && (
            <div className="mb-4 grid grid-cols-2 gap-4">
              <Card
                className={`bg-blue-50 border-blue-200 cursor-pointer transition-all hover:shadow-md ${dateFilter === 'today' ? 'ring-2 ring-blue-500 shadow-md' : ''}`}
                onClick={() => handleDateCardClick('today')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-700">Today's Commitments</span>
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="text-3xl font-bold text-blue-800">{verificationStats.todayCount}</div>
                  {verificationStats.todayPendingVerification > 0 ? (
                    <p className="text-xs text-blue-600 mt-1">
                      {verificationStats.todayPendingVerification} pending verification
                    </p>
                  ) : verificationStats.todayCount > 0 ? (
                    <p className="text-xs text-green-600 mt-1">✓ All verified</p>
                  ) : null}
                  {dateFilter === 'today' && <p className="text-xs text-blue-700 mt-2 font-medium">✓ Showing only today's</p>}
                </CardContent>
              </Card>
              <Card
                className={`cursor-pointer transition-all hover:shadow-md ${verificationStats.pastCount > 0 ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"} ${dateFilter === 'past' ? 'ring-2 ring-red-500 shadow-md' : ''}`}
                onClick={() => handleDateCardClick('past')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${verificationStats.pastCount > 0 ? 'text-red-700' : 'text-gray-600'}`}>
                      Past Commitments (Overdue)
                    </span>
                    <Clock className={`w-5 h-5 ${verificationStats.pastCount > 0 ? 'text-red-600' : 'text-gray-500'}`} />
                  </div>
                  <div className={`text-3xl font-bold ${verificationStats.pastCount > 0 ? 'text-red-800' : 'text-gray-600'}`}>
                    {verificationStats.pastCount}
                  </div>
                  {verificationStats.pastPendingVerification > 0 && (
                    <p className="text-xs text-red-600 mt-1 font-medium">
                      ⚠ {verificationStats.pastPendingVerification} overdue - verify & fulfill first!
                    </p>
                  )}
                  {dateFilter === 'past' && <p className="text-xs text-red-700 mt-2 font-medium">✓ Showing only past</p>}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Warning banner when commitments are pending Manager verification */}
          {verificationStats.verifiedByTL > 0 && (
            <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-orange-800">
                    Cannot Fulfill - Waiting for Manager Verification
                  </p>
                  <p className="text-xs text-orange-700 mt-1">
                    You have {verificationStats.verifiedByTL} commitment{verificationStats.verifiedByTL > 1 ? 's' : ''} that you've verified, but {verificationStats.verifiedByTL > 1 ? 'they need' : 'it needs'} Manager approval before you can fulfill.
                  </p>
                </div>
              </div>
              {Object.keys(verificationStats.pendingByManager).length > 0 && (
                <div className="mt-3 p-3 bg-white rounded border border-orange-200">
                  <p className="text-xs font-medium text-orange-800 mb-2">What to do next:</p>
                  <div className="space-y-2">
                    {Object.entries(verificationStats.pendingByManager).map(([manager, count]) => (
                      <div key={manager} className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-orange-700">
                          <Phone className="w-4 h-4" />
                          <span>Contact <strong>{manager}</strong> to verify {count} commitment{count > 1 ? 's' : ''}</span>
                        </div>
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">Action Required</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Once Manager verifies, the Fulfill button will be enabled.</p>
                </div>
              )}
            </div>
          )}

          {/* Insight Cards (single-day metrics - hide when All Time selected) */}
          {datePreset !== 'all' && insightsLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-6" data-testid="insight-cards-grid">
              {[...Array(7)].map((_, i) => (
                <Card key={i} className="animate-pulse bg-gray-100 h-24" />
              ))}
            </div>
          ) : datePreset !== 'all' && insights ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-6" data-testid="insight-cards-grid">
              <InsightCard
                title="Total Expected"
                value={insights.totalExpected}
                previousValue={insights.prevTotalExpected}
                testId="insight-card-total-expected"
              />
              <InsightCard
                title="Fulfilled"
                value={insights.fulfilled}
                previousValue={insights.prevFulfilled}
                testId="insight-card-fulfilled"
              />
              <InsightCard
                title="Conversion Rate"
                value={insights.conversionRate}
                previousValue={insights.prevConversionRate}
                format="percent"
                testId="insight-card-conversion-rate"
              />
              <InsightCard
                title="Avg Commitment"
                value={insights.avgCommitment}
                previousValue={insights.prevAvgCommitment}
                format="currency"
                testId="insight-card-avg-commitment"
              />
              <InsightCard
                title="Revenue Expected"
                value={insights.revenueExpected}
                previousValue={insights.prevRevenueExpected}
                format="currency"
                testId="insight-card-revenue-expected"
              />
              <InsightCard
                title="Revenue Done"
                value={insights.revenueDone}
                previousValue={insights.prevRevenueDone}
                format="currency"
                testId="insight-card-revenue-done"
              />
              {/* NI Metric Card */}
              <Card className="bg-white" data-testid="insight-card-not-interested">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Not Interested</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end justify-between">
                    <p className="text-2xl font-bold text-gray-500">
                      {insights.ni_count || 0}
                    </p>
                    <Ban className="w-4 h-4 text-gray-400" />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {insights.ni_approved_count || 0} approved, {insights.ni_pending_count || 0} pending
                  </p>
                  {insights.totalExpected > 0 && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {Math.round(((insights.ni_count || 0) / insights.totalExpected) * 100)}% of total
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : null}

          {/* Comprehensive Analytics (when viewing range) */}
          {comprehensiveLoading ? (
            <div className="mb-6 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {[...Array(6)].map((_, i) => (
                  <Card key={i} className="animate-pulse bg-gray-100 h-24" />
                ))}
              </div>
            </div>
          ) : comprehensiveInsights ? (
            <div className="mb-6 space-y-4">
              {/* Range Indicator */}
              {datePreset !== 'today' && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Calendar className="w-4 h-4" />
                  <span>
                    Showing {datePreset === 'all' ? 'all-time' : `${startDate} to ${endDate}`} data
                    ({comprehensiveInsights.totalCommitments} commitments)
                  </span>
                </div>
              )}

              {/* Row 1: Core Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="text-xs text-blue-600 font-medium">Total Commitments</div>
                    <div className="text-2xl font-bold text-blue-800">{comprehensiveInsights.totalCommitments}</div>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <div className="text-xs text-green-600 font-medium">Fulfilled</div>
                    <div className="text-2xl font-bold text-green-800">{comprehensiveInsights.fulfilled}</div>
                    <div className="text-xs text-green-500">{comprehensiveInsights.fulfillmentRate}% rate</div>
                  </CardContent>
                </Card>
                <Card className="bg-yellow-50 border-yellow-200">
                  <CardContent className="p-4">
                    <div className="text-xs text-yellow-600 font-medium">Pending</div>
                    <div className="text-2xl font-bold text-yellow-800">{comprehensiveInsights.pending}</div>
                    <div className="text-xs text-yellow-500">{comprehensiveInsights.partial} partial</div>
                  </CardContent>
                </Card>
                <Card className="bg-gray-50 border-gray-200">
                  <CardContent className="p-4">
                    <div className="text-xs text-gray-600 font-medium">Not Interested</div>
                    <div className="text-2xl font-bold text-gray-700">{comprehensiveInsights.notInterested}</div>
                    <div className="text-xs text-gray-500">{comprehensiveInsights.niPending} pending approval</div>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-4">
                    <div className="text-xs text-purple-600 font-medium">Revenue Expected</div>
                    <div className="text-xl font-bold text-purple-800">{formatCurrency(comprehensiveInsights.revenueExpected)}</div>
                    <div className="text-xs text-purple-500">{formatCurrency(comprehensiveInsights.revenueDone)} done</div>
                  </CardContent>
                </Card>
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-4">
                    <div className="text-xs text-orange-600 font-medium">Revenue at Risk</div>
                    <div className="text-xl font-bold text-orange-800">{formatCurrency(comprehensiveInsights.revenueAtRisk)}</div>
                    <div className="text-xs text-orange-500">Pending commitments</div>
                  </CardContent>
                </Card>
              </div>

              {/* Row 2: Movement Stats */}
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                <Card>
                  <CardContent className="p-3">
                    <div className="text-xs text-gray-600 font-medium">Never Moved</div>
                    <div className="text-xl font-bold text-gray-800">{comprehensiveInsights.movementBreakdown?.never || 0}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <div className="text-xs text-yellow-600 font-medium">Moved 1x</div>
                    <div className="text-xl font-bold text-yellow-700">{comprehensiveInsights.movementBreakdown?.once || 0}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <div className="text-xs text-orange-600 font-medium">Moved 2x</div>
                    <div className="text-xl font-bold text-orange-700">{comprehensiveInsights.movementBreakdown?.twice || 0}</div>
                  </CardContent>
                </Card>
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-3">
                    <div className="text-xs text-red-600 font-medium">Max Moves (3x)</div>
                    <div className="text-xl font-bold text-red-800">{comprehensiveInsights.movementBreakdown?.thrice || 0}</div>
                    <div className="text-xs text-red-500">Cannot move again</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <div className="text-xs text-gray-600 font-medium">Total Moved</div>
                    <div className="text-xl font-bold">{comprehensiveInsights.totalMoved}</div>
                    <div className="text-xs text-gray-500">{comprehensiveInsights.moveRate}% move rate</div>
                  </CardContent>
                </Card>
              </div>

              {/* Cheater Detection Section */}
              {comprehensiveInsights.cheaterDetection?.totalFlags > 0 && (
                <Card className="border-red-200 bg-red-50/30">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                        Suspicious Activity Detection
                      </CardTitle>
                      <Badge variant="destructive">{comprehensiveInsights.cheaterDetection.totalFlags} flags</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* High Move Rate TLs */}
                      {comprehensiveInsights.cheaterDetection.highMoveRateTLs?.length > 0 && (
                        <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                          <div className="flex items-center gap-2 mb-2">
                            <MoveRight className="w-4 h-4 text-orange-600" />
                            <span className="text-sm font-medium text-orange-800">High Move Rate (&gt;30%)</span>
                          </div>
                          <div className="space-y-1">
                            {comprehensiveInsights.cheaterDetection.highMoveRateTLs.slice(0, 5).map((tl, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span className="text-orange-700 truncate">{tl.name}</span>
                                <span className="font-medium text-orange-900">{tl.rate}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* High NI Rate TLs */}
                      {comprehensiveInsights.cheaterDetection.highNIRateTLs?.length > 0 && (
                        <div className="p-3 bg-gray-100 rounded-lg border border-gray-300">
                          <div className="flex items-center gap-2 mb-2">
                            <Ban className="w-4 h-4 text-gray-600" />
                            <span className="text-sm font-medium text-gray-800">High NI Rate (&gt;20%)</span>
                          </div>
                          <div className="space-y-1">
                            {comprehensiveInsights.cheaterDetection.highNIRateTLs.slice(0, 5).map((tl, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span className="text-gray-700 truncate">{tl.name}</span>
                                <span className="font-medium text-gray-900">{tl.rate}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Max Moves Still Pending */}
                      {comprehensiveInsights.cheaterDetection.maxMovesPending > 0 && (
                        <div className="p-3 bg-red-100 rounded-lg border border-red-300">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertCircle className="w-4 h-4 text-red-600" />
                            <span className="text-sm font-medium text-red-800">At Max Moves (Stuck)</span>
                          </div>
                          <div className="text-2xl font-bold text-red-900">{comprehensiveInsights.cheaterDetection.maxMovesPending}</div>
                          <div className="text-xs text-red-600 mt-1">{formatCurrency(comprehensiveInsights.cheaterDetection.maxMovesRevenue)} at risk</div>
                        </div>
                      )}

                      {/* Top Movers */}
                      {comprehensiveInsights.cheaterDetection.topMovers?.length > 0 && (
                        <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="w-4 h-4 text-purple-600" />
                            <span className="text-sm font-medium text-purple-800">Top Movers (by count)</span>
                          </div>
                          <div className="space-y-1">
                            {comprehensiveInsights.cheaterDetection.topMovers.slice(0, 5).map((tl, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span className="text-purple-700 truncate">{tl.name}</span>
                                <span className="font-medium text-purple-900">{tl.count} moves</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Low Fulfillment TLs */}
                      {comprehensiveInsights.cheaterDetection.lowFulfillmentTLs?.length > 0 && (
                        <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                          <div className="flex items-center gap-2 mb-2">
                            <TrendingDown className="w-4 h-4 text-yellow-600" />
                            <span className="text-sm font-medium text-yellow-800">Low Fulfillment (&lt;50%)</span>
                          </div>
                          <div className="space-y-1">
                            {comprehensiveInsights.cheaterDetection.lowFulfillmentTLs.slice(0, 5).map((tl, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span className="text-yellow-700 truncate">{tl.name}</span>
                                <span className="font-medium text-yellow-900">{tl.rate}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : null}

          {/* Verification Status Cards - Clickable */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card
              className={`cursor-pointer transition-all hover:shadow-md ${verificationFilter === 'verifiedByTL' ? 'ring-2 ring-yellow-500 shadow-md' : ''}`}
              onClick={() => handleVerificationCardClick('verifiedByTL')}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Verified by TL Only
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold">{verificationStats.verifiedByTL}</div>
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Awaiting Manager verification</p>
                {verificationFilter === 'verifiedByTL' && <p className="text-xs text-yellow-600 mt-1 font-medium">✓ Filtering</p>}
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all hover:shadow-md ${verificationFilter === 'verifiedByManager' ? 'ring-2 ring-orange-500 shadow-md' : ''}`}
              onClick={() => handleVerificationCardClick('verifiedByManager')}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Verified by Manager Only
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold">{verificationStats.verifiedByManager}</div>
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">TL verification missing (rare)</p>
                {verificationFilter === 'verifiedByManager' && <p className="text-xs text-orange-600 mt-1 font-medium">✓ Filtering</p>}
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all hover:shadow-md ${verificationFilter === 'fullyVerified' ? 'ring-2 ring-green-500 shadow-md' : ''}`}
              onClick={() => handleVerificationCardClick('fullyVerified')}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Fully Verified
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-green-600">{verificationStats.verifiedByBoth}</div>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Ready for fulfillment</p>
                {verificationFilter === 'fullyVerified' && <p className="text-xs text-green-600 mt-1 font-medium">✓ Filtering</p>}
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all hover:shadow-md ${verificationFilter === 'unverified' ? 'ring-2 ring-red-500 shadow-md' : ''}`}
              onClick={() => handleVerificationCardClick('unverified')}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Unverified
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-red-600">{verificationStats.unverified}</div>
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Needs TL and Manager verification</p>
                {verificationFilter === 'unverified' && <p className="text-xs text-red-600 mt-1 font-medium">✓ Filtering</p>}
              </CardContent>
            </Card>
          </div>

          {/* WTF Lead Funnel Cards */}
          {wtfLeadData && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg border-2 border-purple-200 p-4">
                <p className="text-xs text-purple-600 font-medium uppercase">WTF Converted Leads</p>
                <p className="text-2xl font-bold">{wtfLeadData.convertedLeads.toLocaleString('en-IN')}</p>
                <WtfSyncBadge lastSync={wtfSync} stale={wtfStale} size="sm" />
              </div>
              <div className="bg-white rounded-lg border-2 border-purple-200 p-4">
                <p className="text-xs text-purple-600 font-medium uppercase">WTF Today's Leads</p>
                <p className="text-2xl font-bold">{wtfLeadData.todaysLeads}</p>
              </div>
              <div className="bg-white rounded-lg border-2 border-purple-200 p-4">
                <p className="text-xs text-purple-600 font-medium uppercase">WTF Walk-ins</p>
                <p className="text-2xl font-bold">{wtfLeadData.totalWalkins}</p>
              </div>
              <div className="bg-white rounded-lg border-2 border-purple-200 p-4">
                <p className="text-xs text-purple-600 font-medium uppercase">WTF All Leads</p>
                <p className="text-2xl font-bold">{wtfLeadData.allLeads.toLocaleString('en-IN')}</p>
              </div>
            </div>
          )}

          {/* Active Filters Display */}
          {(dateFilter !== 'all' || verificationFilter !== 'all') && (
            <div className="mb-4 flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-600">Active filters:</span>
              {dateFilter !== 'all' && (
                <Badge
                  variant="secondary"
                  className="cursor-pointer hover:bg-gray-200"
                  onClick={() => setDateFilter('all')}
                >
                  {dateFilter === 'today' ? "Today's" : 'Past'} commitments ×
                </Badge>
              )}
              {verificationFilter !== 'all' && (
                <Badge
                  variant="secondary"
                  className="cursor-pointer hover:bg-gray-200"
                  onClick={() => setVerificationFilter('all')}
                >
                  {verificationFilter === 'verifiedByTL' && 'TL Verified Only'}
                  {verificationFilter === 'verifiedByManager' && 'Manager Verified Only'}
                  {verificationFilter === 'fullyVerified' && 'Fully Verified'}
                  {verificationFilter === 'unverified' && 'Unverified'} ×
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-6"
                onClick={() => { setDateFilter('all'); setVerificationFilter('all'); }}
              >
                Clear all
              </Button>
            </div>
          )}

          {/* Filter Tabs */}
          <Tabs value={filter} onValueChange={setFilter} className="mb-4">
            <TabsList>
              <TabsTrigger value="pending">Pending</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="fulfilled">Fulfilled</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Collapsible Multi-Dimensional Filter Bar */}
          <CommitmentFilterBar
            filters={advancedFilters}
            setFilters={setAdvancedFilters}
            options={filterOptions}
            activeFilterCount={activeFilterCount}
          />

          {/* Commitments Table */}
          {loading ? (
            <div className="text-center py-8">Loading commitments...</div>
          ) : advancedFilteredCommitments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {commitments.length === 0
                ? (selectedDate ? `No commitments found for ${selectedDate}` : 'No commitments found')
                : `No commitments match the selected filters (${commitments.length} total${activeFilterCount > 0 ? `, ${activeFilterCount} advanced filters active` : ''})`}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Pulse</TableHead>
                    <TableHead>Gym</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Closure Expected</TableHead>
                    <TableHead className="text-right">Closure Done</TableHead>
                    <TableHead className="text-right">Revenue Expected</TableHead>
                    <TableHead className="text-right">Revenue Done</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center w-24">NI Approval</TableHead>
                    <TableHead className="text-center w-24">TL Verified</TableHead>
                    <TableHead className="text-center w-24">Mgr Verified</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {advancedFilteredCommitments.map((commitment) => {
                    const isNI = commitment.commitment_status === 'not_interested';
                    const isNIApproved = isNI && commitment.ni_approved;
                    const rowClass = isNIApproved
                      ? 'bg-gray-50 opacity-70'
                      : isNI
                        ? 'bg-gray-50'
                        : commitment.commitment_status === 'pending'
                          ? 'bg-yellow-50'
                          : '';
                    return (
                    <TableRow
                      key={commitment.id}
                      className={rowClass}
                    >
                      <TableCell>{commitment.entry_date}</TableCell>
                      <TableCell>{commitment.pulse_name}</TableCell>
                      <TableCell className="font-medium">
                        <span className={isNIApproved ? 'line-through text-gray-400' : ''}>
                          {commitment.gym_name}
                        </span>
                        {/* Movement badge - show if commitment has been moved */}
                        {commitment.is_moved && commitment.display_badge && (
                          <Badge variant="secondary" className="ml-2 text-xs">
                            <ArrowRight className="w-3 h-3 mr-1" />
                            {commitment.display_badge}
                          </Badge>
                        )}
                        {/* NI reason badge */}
                        {isNI && commitment.not_interested_reason && (
                          <span className="block text-xs text-gray-400 mt-0.5" title={commitment.not_interested_reason}>
                            NI: {commitment.not_interested_reason.length > 30 ? commitment.not_interested_reason.slice(0, 30) + '...' : commitment.not_interested_reason}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{commitment.source_name}</TableCell>
                      <TableCell className={`text-right font-semibold ${isNIApproved ? 'line-through text-gray-400' : ''}`}>
                        {commitment.closure_expected}
                      </TableCell>
                      <TableCell className="text-right">
                        {commitment.closure_done || 0}
                      </TableCell>
                      <TableCell className={`text-right font-semibold ${isNIApproved ? 'line-through text-gray-400' : ''}`}>
                        {formatCurrency(commitment.revenue_expected)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(commitment.revenue_done || 0)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(commitment.commitment_status, commitment)}
                      </TableCell>
                      <TableCell className="text-center">
                        <NIApprovalControl
                          commitment={commitment}
                          currentUser={currentUser}
                          onApprove={() => fetchCommitments()}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <VerificationControls
                          commitment={commitment}
                          currentUser={currentUser}
                          onVerify={handleVerificationComplete}
                          type="tl"
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <VerificationControls
                          commitment={commitment}
                          currentUser={currentUser}
                          onVerify={handleVerificationComplete}
                          type="manager"
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1 flex-wrap">
                          {/* Not Interested action - for pending/partial (TL/Manager/Admin) */}
                          {!isNI && (commitment.commitment_status === 'pending' || commitment.commitment_status === 'partial') &&
                           (currentUser?.role === 'team_leader' || currentUser?.role === 'manager' || currentUser?.role === 'admin') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleNotInterested(commitment)}
                              title="Mark as Not Interested"
                              className="text-gray-500 hover:text-gray-700"
                            >
                              <Ban className="w-4 h-4 mr-1" />
                              NI
                            </Button>
                          )}
                          {/* Reverse NI action - for not_interested entries */}
                          {isNI && (currentUser?.role === 'team_leader' || currentUser?.role === 'manager' || currentUser?.role === 'admin') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleReverseNI(commitment.id)}
                              title="Reverse Not Interested - restore to pending"
                              className="text-blue-500 hover:text-blue-700"
                            >
                              <Undo2 className="w-4 h-4 mr-1" />
                              Restore
                            </Button>
                          )}
                          {/* Fulfill - hide for NI entries */}
                          {!isNI && (
                            <Button
                              size="sm"
                              variant={(!commitment.commitment_tl_verified || !commitment.commitment_mgr_verified) ? "ghost" : "outline"}
                              onClick={() => handleFulfillClick(commitment)}
                              className={(!commitment.commitment_tl_verified || !commitment.commitment_mgr_verified) ? "opacity-60 border-dashed" : ""}
                              title={(!commitment.commitment_tl_verified || !commitment.commitment_mgr_verified) ? "Click to see what's blocking fulfillment" : "Update commitment fulfillment"}
                              data-track="commitment-fulfill"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Fulfill
                            </Button>
                          )}
                          {/* Move to Date button - only for pending/partial commitments */}
                          {!isNI && (commitment.commitment_status === 'pending' || commitment.commitment_status === 'partial') && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedCommitment(commitment);
                                setMoveDialogOpen(true);
                              }}
                              title="Move commitment to a future date"
                              data-track="commitment-move"
                            >
                              <ArrowRight className="w-4 h-4 mr-1" />
                              Move
                            </Button>
                          )}
                          {/* Timeline button - show if commitment has movement history */}
                          {commitment.movements && commitment.movements.length > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedCommitment(commitment);
                                setTimelineDialogOpen(true);
                              }}
                              title="View movement history"
                            >
                              <Clock className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editingId !== null} onOpenChange={() => setEditingId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Commitment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="closure_done">Closure Done</Label>
              <Input
                id="closure_done"
                type="number"
                min="0"
                value={editForm.closure_done}
                onChange={(e) => setEditForm({ ...editForm, closure_done: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="revenue_done">Revenue Done (₹)</Label>
              <Input
                id="revenue_done"
                type="number"
                min="0"
                step="1000"
                value={editForm.revenue_done}
                onChange={(e) => setEditForm({ ...editForm, revenue_done: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingId(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Commitment Dialog */}
      {selectedCommitment && (
        <MoveCommitmentDialog
          commitment={selectedCommitment}
          open={moveDialogOpen}
          onClose={() => {
            setMoveDialogOpen(false);
            setSelectedCommitment(null);
          }}
          onMoveSuccess={() => {
            fetchCommitments();
          }}
        />
      )}

      {/* Timeline Dialog */}
      {selectedCommitment && (
        <Dialog open={timelineDialogOpen} onOpenChange={setTimelineDialogOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>
                Commitment Movement History - {selectedCommitment.gym_name}
              </DialogTitle>
            </DialogHeader>
            <CommitmentTimeline commitment={selectedCommitment} />
          </DialogContent>
        </Dialog>
      )}

      {/* Fulfillment Blocked Modal */}
      <FulfillmentBlockedModal
        open={blockedModalOpen}
        onClose={() => {
          setBlockedModalOpen(false);
          setSelectedCommitment(null);
        }}
        commitment={selectedCommitment}
        currentUser={currentUser}
        onVerificationComplete={() => {
          fetchCommitments();
        }}
      />

      {/* Not Interested Dialog */}
      <NotInterestedDialog
        open={niDialogOpen}
        onOpenChange={setNiDialogOpen}
        commitment={niTargetCommitment}
        onSuccess={() => {
          fetchCommitments();
          setNiTargetCommitment(null);
        }}
      />
    </div>
  );
};

export default CommitmentDashboard;
